export interface LocationResult {
  latitude: number;
  longitude: number;
  name?: string;
}

class LocationService {
  async getCurrentLocation(): Promise<LocationResult | null> {
    if (!('geolocation' in navigator)) {
      return null;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const result: LocationResult = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };

          // Try to get location name via reverse geocoding
          try {
            const name = await this.reverseGeocode(result.latitude, result.longitude);
            if (name) {
              result.name = name;
            }
          } catch (error) {
            console.error('Reverse geocoding failed:', error);
          }

          resolve(result);
        },
        (error) => {
          console.error('Geolocation error:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }

  private async reverseGeocode(lat: number, lon: number): Promise<string | null> {
    try {
      // Using Nominatim (OpenStreetMap) - free, no API key required
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=14`,
        {
          headers: {
            'User-Agent': 'PitchCounter/1.0',
          },
        }
      );

      if (!response.ok) return null;

      const data = await response.json();

      // Build a friendly name from address components
      const address = data.address;
      if (!address) return null;

      // Prioritize: suburb/neighbourhood > city > town > village
      const area =
        address.suburb ||
        address.neighbourhood ||
        address.city ||
        address.town ||
        address.village ||
        address.municipality;

      return area || null;
    } catch {
      return null;
    }
  }

  generateGameName(locationName?: string, userName?: string): string {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const timeStr = now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });

    // Extract first name from display name
    const firstName = userName?.split(' ')[0];

    if (locationName && firstName) {
      return `${locationName} - ${dateStr} ${timeStr} - ${firstName}`;
    }

    if (locationName) {
      return `${locationName} - ${dateStr} ${timeStr}`;
    }

    if (firstName) {
      return `Game - ${dateStr} ${timeStr} - ${firstName}`;
    }

    return `Game - ${dateStr} ${timeStr}`;
  }
}

export const locationService = new LocationService();
